import { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";

/**
 * Google Analytics 4 loader — respects cookie consent and reacts to consent changes.
 */
function readConsent() {
  try {
    const pref = JSON.parse(localStorage.getItem("cw_cookie_pref_v1") || "null");
    return pref?.choice === "accepted";
  } catch {
    return false;
  }
}

export default function GoogleAnalytics() {
  const location = useLocation();
  const MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
  const [consented, setConsented] = useState(() => readConsent());

  const bootGA = useCallback(() => {
    if (!MEASUREMENT_ID || window.__cw_ga_loaded) return;
    window.__cw_ga_loaded = true;
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
    document.head.appendChild(s);
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() { window.dataLayer.push(arguments); };
    window.gtag("js", new Date());
    window.gtag("config", MEASUREMENT_ID, { anonymize_ip: true, send_page_view: false });
  }, [MEASUREMENT_ID]);

  // Boot when consent is (or becomes) granted
  useEffect(() => {
    if (consented) bootGA();
  }, [consented, bootGA]);

  // Listen to consent changes (banner event or cross-tab storage event)
  useEffect(() => {
    const onConsent = (e) => setConsented(e?.detail?.choice === "accepted" || readConsent());
    const onStorage = (e) => {
      if (e.key === "cw_cookie_pref_v1") setConsented(readConsent());
    };
    window.addEventListener("cw:cookie-consent", onConsent);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("cw:cookie-consent", onConsent);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  // Page view on route change (only when GA is active)
  useEffect(() => {
    if (!MEASUREMENT_ID || !window.gtag) return;
    window.gtag("event", "page_view", {
      page_path: location.pathname + location.search,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [location.pathname, location.search, MEASUREMENT_ID]);

  return null;
}
